/**
 * IdeasCard — Voice of User (Feb 2026)
 * =====================================
 *
 * Lives on the teen + adult dashboard. CTA opens a modal where the
 * user can write any product idea they want to see in HiBFF. Chosen
 * ideas are rewarded with an exclusive NFT — that's the headline copy.
 *
 * The modal is also opened from the TopNav search "no results" panel
 * via a `hibff:open-idea-modal` custom event with an optional
 * `sourceQuery` in detail, so the textarea pre-fills with what the
 * user originally searched for.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Lightbulb, Sparkles, X, Award, Loader2, CheckCircle2 } from 'lucide-react';
import { apiClient } from '../../api/client';

const IdeasCard = () => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [sourceQuery, setSourceQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({ total: 0, nft_awarded_count: 0 });

  const refreshStats = useCallback(async () => {
    try {
      const data = await apiClient.request('/api/ideas/mine');
      setStats({
        total: data?.total || 0,
        nft_awarded_count: data?.nft_awarded_count || 0,
      });
    } catch {
      // Soft-fail — stats are non-critical.
    }
  }, []);

  useEffect(() => { refreshStats(); }, [refreshStats]);

  // Cross-component opener — TopNav fires this from the "no results" pane.
  useEffect(() => {
    const onOpen = (ev) => {
      const q = ev?.detail?.sourceQuery || '';
      setSourceQuery(q);
      if (q) setText(`I was looking for "${q}" — could we have…\n`);
      setOpen(true);
    };
    window.addEventListener('hibff:open-idea-modal', onOpen);
    return () => window.removeEventListener('hibff:open-idea-modal', onOpen);
  }, []);

  const handleClose = () => {
    setOpen(false);
    setSubmittedId(null);
    setError('');
    setText('');
    setSourceQuery('');
  };

  const handleSubmit = async () => {
    if (text.trim().length < 8) {
      setError('Tell us a bit more — at least a sentence.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const data = await apiClient.request('/api/ideas', {
        method: 'POST',
        body: JSON.stringify({
          idea_text: text.trim(),
          source_query: sourceQuery || undefined,
        }),
      });
      if (data?.success) {
        setSubmittedId(data.idea.id);
        refreshStats();
      } else {
        setError('Something went wrong — try again in a moment.');
      }
    } catch (e) {
      setError(e?.message || 'Could not submit your idea. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="rounded-3xl p-6 shadow-sm relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #FFF7ED 0%, #FFE4D1 100%)',
          border: '1px solid #FED7AA',
        }}
        data-testid="ideas-card"
      >
        <div className="absolute -right-4 -top-4 w-28 h-28 rounded-full opacity-20" style={{ background: '#F5831F' }} />
        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb size={20} style={{ color: '#F5831F' }} />
            <span className="text-[11px] uppercase tracking-wider font-bold" style={{ color: '#F5831F' }}>
              Got an Idea?
            </span>
          </div>
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 leading-snug">
            Help shape what HiBFF builds next.
          </h3>
          <p className="text-sm text-gray-700 mb-4 leading-relaxed">
            Tell us anything you&apos;d love to see on your dashboard or in your AvA chats — features, vibes, wild ideas. <span className="font-semibold" style={{ color: '#7B4FD8' }}>Chosen ideas earn an exclusive HiBFF NFT.</span>
          </p>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full font-semibold text-sm text-white shadow-sm hover:shadow-md transition-all"
              style={{ background: 'linear-gradient(135deg, #F5831F 0%, #DC2626 100%)' }}
              data-testid="ideas-card-open-btn"
            >
              <Sparkles size={15} />
              Share Your Idea
            </button>
            {stats.total > 0 && (
              <div className="text-xs text-gray-600" data-testid="ideas-card-stats">
                You&apos;ve submitted <span className="font-bold text-gray-900">{stats.total}</span>
                {stats.nft_awarded_count > 0 && (
                  <> · <span className="font-bold" style={{ color: '#7B4FD8' }}>🏆 {stats.nft_awarded_count} NFT{stats.nft_awarded_count !== 1 ? 's' : ''}</span></>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Submit modal */}
      {open && createPortal(
        <div
          className="fixed inset-0 z-[9600] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={handleClose}
          data-testid="ideas-modal-backdrop"
        >
          <div
            className="bg-white rounded-3xl shadow-2xl w-full max-w-lg p-6 sm:p-7 relative"
            onClick={(e) => e.stopPropagation()}
            data-testid="ideas-modal"
          >
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close"
              className="absolute top-3 right-3 w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500"
              data-testid="ideas-modal-close"
            >
              <X size={18} />
            </button>

            {submittedId ? (
              <div className="text-center py-6" data-testid="ideas-modal-success">
                <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ background: '#DCFCE7' }}>
                  <CheckCircle2 size={28} style={{ color: '#16A34A' }} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Idea received!</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-5">
                  Robert reads every single idea personally. If yours is chosen, you&apos;ll get an exclusive HiBFF NFT in your vault.
                </p>
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-5 py-2.5 rounded-full font-semibold text-sm text-white"
                  style={{ background: '#7B4FD8' }}
                  data-testid="ideas-modal-success-close"
                >
                  Got it
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <Lightbulb size={18} style={{ color: '#F5831F' }} />
                  <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500">
                    Voice of HiBFF
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 leading-snug">
                  What would you love to see next?
                </h3>
                <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                  No idea is too small. A feature, a chat vibe, a quest, an event — anything.
                  <span className="block mt-1 font-semibold" style={{ color: '#7B4FD8' }}>
                    <Award size={13} className="inline -mt-0.5 mr-1" />
                    Chosen ideas are rewarded with an exclusive HiBFF NFT.
                  </span>
                </p>

                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="e.g., I'd love a 60-second mood reset breathing exercise right on the dashboard…"
                  rows={5}
                  maxLength={1200}
                  className="w-full p-3 rounded-xl border border-gray-200 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 outline-none text-sm resize-none text-gray-800"
                  data-testid="ideas-modal-textarea"
                />
                <div className="flex items-center justify-between mt-1 text-[11px] text-gray-400">
                  <span>{text.length} / 1200</span>
                  {sourceQuery && <span>From search: &ldquo;{sourceQuery}&rdquo;</span>}
                </div>
                {error && (
                  <div className="mt-2 text-sm text-red-600" data-testid="ideas-modal-error">{error}</div>
                )}

                <div className="flex items-center justify-end gap-2 mt-5">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 rounded-full text-sm font-semibold text-gray-600 hover:bg-gray-100"
                    data-testid="ideas-modal-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting || text.trim().length < 8}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: 'linear-gradient(135deg, #F5831F 0%, #DC2626 100%)' }}
                    data-testid="ideas-modal-submit"
                  >
                    {submitting ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                    {submitting ? 'Sending…' : 'Submit Idea'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default IdeasCard;
